/**
 * Resume Studio — "My CVs" recovery modal.
 *
 * Guest users who paid/previewed but lost the URL / cleared localStorage
 * / are on a new device recover past sessions by verifying ownership of
 * their email via a 6-digit OTP.
 *
 * Backend contract: POST /api/resume-studio/lookup-request → 200
 * {sent:true} · POST /api/resume-studio/lookup-verify with
 * {email, code} → 200 {verified:true, sessions:[...]} (up to 5 rows).
 */
import React, { useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import * as rsSession from "../../lib/resumeStudioSession";

export default function MyCvsLookupModal({ onClose }) {
    const [step, setStep] = useState("email"); // email → code → results
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [sessions, setSessions] = useState([]);

    const requestCode = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!email || !email.includes("@")) {
            setError("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/resume-studio/lookup-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim().toLowerCase() }),
            });
            if (!r.ok) throw new Error("Could not send code");
            setStep("code");
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    const verifyCode = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (code.length !== 6) {
            setError("Code must be 6 digits");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/resume-studio/lookup-verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email.trim().toLowerCase(),
                    code: code.trim(),
                }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Invalid code");
            setSessions(d.sessions || []);
            setStep("results");
        } catch (err) {
            setError(err.message || "Verification failed");
        } finally {
            setBusy(false);
        }
    };

    const openSession = (sid) => {
        window.location.href = `/resume?session=${sid}`;
    };

    return (
        <div
            data-testid="my-cvs-modal"
            className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
                    aria-label="Close"
                >×</button>

                {step === "email" && (
                    <form onSubmit={requestCode} data-testid="my-cvs-step-email">
                        <div className="w-11 h-11 rounded-xl bg-cobalt-50 text-cobalt-600 grid place-items-center mb-3">
                            <MagnifyingGlass weight="bold" size={22} />
                        </div>
                        <h2 className="text-lg font-bold">Find your past CVs</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Enter the email you used earlier. We'll send a 6-digit code.
                        </p>
                        <input
                            data-testid="my-cvs-email-input"
                            type="email"
                            className="mt-4 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-cobalt-500 focus:ring-1 focus:ring-cobalt-500 outline-none"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                            required
                        />
                        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
                        <button
                            data-testid="my-cvs-send-code-btn"
                            type="submit"
                            disabled={busy}
                            className="mt-4 w-full bg-cobalt-600 hover:bg-cobalt-700 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg"
                        >
                            {busy ? "Sending…" : "Send code"}
                        </button>
                    </form>
                )}

                {step === "code" && (
                    <form onSubmit={verifyCode} data-testid="my-cvs-step-code">
                        <div className="w-11 h-11 rounded-xl bg-cobalt-50 text-cobalt-600 grid place-items-center mb-3">
                            <MagnifyingGlass weight="bold" size={22} />
                        </div>
                        <h2 className="text-lg font-bold">Enter your code</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Sent to <b>{email}</b>. Expires in 10 minutes.
                        </p>
                        <input
                            data-testid="my-cvs-code-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            className="mt-4 w-full font-mono text-center text-2xl tracking-[0.5em] border border-slate-200 rounded-lg px-3 py-3 focus:border-cobalt-500 focus:ring-1 focus:ring-cobalt-500 outline-none"
                            placeholder="••••••"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            autoFocus
                        />
                        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
                        <button
                            data-testid="my-cvs-verify-btn"
                            type="submit"
                            disabled={busy}
                            className="mt-4 w-full bg-cobalt-600 hover:bg-cobalt-700 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg"
                        >
                            {busy ? "Verifying…" : "Verify & unlock"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setStep("email"); setCode(""); setError(""); }}
                            className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700"
                        >
                            ← Change email
                        </button>
                    </form>
                )}

                {step === "results" && (
                    <div data-testid="my-cvs-step-results">
                        <h2 className="text-lg font-bold">Your past CVs</h2>
                        <p className="text-sm text-slate-600 mt-1 mb-4">
                            {sessions.length === 0
                                ? "No CVs found for this email. Start a fresh one below."
                                : `Found ${sessions.length} CV${sessions.length > 1 ? "s" : ""}. Pick one to continue.`}
                        </p>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                            {sessions.map((s) => {
                                // Feb 18, 2026 — Prod bug guard: sessions without a
                                // completed AI rewrite (`preview_available=false`)
                                // can't render a preview PDF and produced the
                                // "prohibited-icon iframe" the user hit. Show them
                                // with a clear DRAFT badge and route them back to
                                // the wizard-recovery flow instead of straight to
                                // the preview page.
                                const ready = s.preview_available !== false;
                                return (
                                    <button
                                        key={s.session_id}
                                        data-testid={`my-cvs-session-${s.session_id}`}
                                        onClick={() => openSession(s.session_id)}
                                        className="w-full text-left border border-slate-200 hover:border-cobalt-500 rounded-lg p-3 transition"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm font-semibold truncate">
                                                {s.target_role || "Untitled CV"}
                                            </div>
                                            {s.payment_status === "paid" ? (
                                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                    PAID · {s.tier?.toUpperCase()}
                                                </span>
                                            ) : !ready ? (
                                                <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                    DRAFT
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                    PREVIEW
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {rsSession.relativeTime(s.created_at)}
                                            {!ready && " · Not yet rewritten — pick up where you left off"}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
